import { Quote, Star, Sparkles, GraduationCap, Heart, Trophy } from "lucide-react";

/**
 * ImpactSection — honest, early-stage positioning.
 * We intentionally do NOT use fabricated numbers. Instead we describe
 * what the platform is designed to do, and invite schools to be part
 * of the first wave.
 */
const POSITIONS = [
  { icon: Sparkles,       t: "Built to develop confidence" },
  { icon: GraduationCap,  t: "Designed for real-world learning" },
  { icon: Trophy,         t: "Created for future-ready students" },
  { icon: Heart,          t: "Pilot-ready for schools and youth programs" },
];

const STORIES = [
  { name: "Sarah M.", role: "Parent",
    text: "My daughter went from shy to confidently pitching her business idea. This program gave her real skills she'll use forever." },
  { name: "Mr. Rodriguez", role: "Middle School CTE Teacher",
    text: "The curriculum is perfectly structured. My students are engaged, and the AI coach handles questions I couldn't answer." },
  { name: "Jennifer K.", role: "Parent",
    text: "Finally, a program that teaches kids about money and business in a fun, age-appropriate way." },
];

export default function ImpactSection() {
  return (
    <section
      id="impact"
      className="relative bg-white py-20 sm:py-28"
      data-testid="impact-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className="text-xs font-black tracking-[0.22em] uppercase text-gray-500 mb-3">
            The Movement Is Just Beginning
          </p>
          <h2 className="text-[32px] md:text-5xl font-black tracking-tighter leading-[1.05] text-black">
            Real Skills.{" "}
            <span className="lsbp-green-chip">Real Confidence.</span>
            <br /> Built For The Future.
          </h2>
          <p className="mt-4 text-base md:text-xl text-gray-700 leading-relaxed">
            The Lemonade Stand Business Plan is built to help students develop
            confidence, creativity, leadership, teamwork, financial literacy,
            and real-world entrepreneurship skills.
          </p>
        </div>

        {/* Editorial collage — visuals only */}
        <div className="max-w-5xl mx-auto mb-10 rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_rgba(0,0,0,0.10)]">
          <img
            src="/assets/landing/impact-collage.png"
            alt="Collage of K-12 students collaborating, pitching, earning certificates, and building real businesses."
            className="w-full h-auto block"
            loading="lazy"
            data-testid="impact-collage-image"
          />
        </div>

        {/* Honest positioning grid — no fabricated metrics */}
        <ul
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-12 max-w-5xl mx-auto"
          data-testid="impact-positions"
        >
          {POSITIONS.map((p) => (
            <li
              key={p.t}
              className="bg-[#FDFBF7] ring-1 ring-black/5 rounded-2xl p-4 flex items-start gap-3"
            >
              <span className="bg-[#FFD500] text-black w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                <p.icon className="w-4 h-4" />
              </span>
              <p className="text-sm font-extrabold text-black leading-snug pt-1.5">
                {p.t}
              </p>
            </li>
          ))}
        </ul>

        {/* Be part of the first wave */}
        <div className="max-w-3xl mx-auto bg-[#0A0A0A] text-white rounded-3xl p-6 sm:p-8 text-center mb-12">
          <p className="text-[11px] font-black tracking-[0.22em] uppercase text-[#FFD500] mb-2">
            Be Part Of The First Schools
          </p>
          <h3 className="text-2xl font-black tracking-tight">
            Bring entrepreneurship + responsible AI learning to your students.
          </h3>
          <p className="mt-3 text-sm text-gray-300 max-w-xl mx-auto">
            We're partnering with the first pilot schools, after-school
            programs, and youth organizations to bring this movement to life.
          </p>
        </div>

        {/* Testimonials */}
        <ul
          className="grid md:grid-cols-3 gap-4"
          aria-label="What people are saying"
          data-testid="impact-testimonials"
        >
          {STORIES.map((s) => (
            <li
              key={s.name}
              className="relative bg-white ring-1 ring-black/10 rounded-3xl p-6"
            >
              <Quote className="absolute -top-3 -left-3 w-7 h-7 text-[#FFD500] fill-[#FFD500]" aria-hidden="true" />
              <p className="text-sm text-gray-800 leading-relaxed mb-4">
                "{s.text}"
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-black text-black text-sm">{s.name}</p>
                  <p className="text-xs text-gray-500 font-bold tracking-wide uppercase">{s.role}</p>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className="w-3.5 h-3.5 fill-[#FFD500] text-[#FFD500]" />
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
